/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CreateManifestUploadTaskResponse } from '../models/CreateManifestUploadTaskResponse';
import type { DataAssetSearchStatisticsResponse } from '../models/DataAssetSearchStatisticsResponse';
import type { DataVolumeCreateRequest } from '../models/DataVolumeCreateRequest';
import type { DataVolumeResponseWithManifest } from '../models/DataVolumeResponseWithManifest';
import type { ManifestResponse } from '../models/ManifestResponse';
import type { ManifestResponseWithDataVolume } from '../models/ManifestResponseWithDataVolume';
import type { ManifestTransferConfigResponse } from '../models/ManifestTransferConfigResponse';
import type { ManifestType } from '../models/ManifestType';
import type { SharedLinkResponse } from '../models/SharedLinkResponse';
import type { SQLModelPage_DataAssetTreeDirectoryResponse_ } from '../models/SQLModelPage_DataAssetTreeDirectoryResponse_';
import type { SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_ } from '../models/SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_';
import type { StorageTier } from '../models/StorageTier';
import type { StorageTierStatus } from '../models/StorageTierStatus';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DataTransferService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Data Volumes
     * @returns DataVolumeResponseWithManifest List data volumes
     * @throws ApiError
     */
    public listDataVolumes({
        ownerUserId,
    }: {
        ownerUserId?: string,
    }): CancelablePromise<Array<DataVolumeResponseWithManifest>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_volume',
            query: {
                'owner_user_id': ownerUserId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Register Data Volume
     * @returns DataVolumeResponseWithManifest Register a new data volume
     * @throws ApiError
     */
    public registerDataVolume({
        requestBody,
    }: {
        requestBody: DataVolumeCreateRequest,
    }): CancelablePromise<DataVolumeResponseWithManifest> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data_volume',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Seal Data Volume
     * @returns DataVolumeResponseWithManifest Mark this manifest as 'sealed', locking it from further updates
     * @throws ApiError
     */
    public sealDataVolume({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<DataVolumeResponseWithManifest> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data_volume/{public_id}/seal',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Unseal Data Volume
     * @returns DataVolumeResponseWithManifest Mark this manifest as 'unsealed', allowing it to be updated in-place
     * @throws ApiError
     */
    public unsealDataVolume({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<DataVolumeResponseWithManifest> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data_volume/{public_id}/unseal',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Data Volume Detail
     * @returns DataVolumeResponseWithManifest Lookup a data volume by public id
     * @throws ApiError
     */
    public dataVolumeDetail({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<DataVolumeResponseWithManifest> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_volume/{public_id}',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Data Volume
     * @returns any Delete a data volume
     * @throws ApiError
     */
    public deleteDataVolume({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/data_volume/{public_id}',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Manifests
     * @returns ManifestResponseWithDataVolume List manifests
     * @throws ApiError
     */
    public listManifests({
        includeDescendantDeployments = false,
        manifestTypes,
        realAssetDeploymentPublicIds,
    }: {
        includeDescendantDeployments?: boolean,
        manifestTypes?: Array<ManifestType>,
        realAssetDeploymentPublicIds?: Array<string>,
    }): CancelablePromise<Array<ManifestResponseWithDataVolume>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/manifest',
            query: {
                'include_descendant_deployments': includeDescendantDeployments,
                'manifest_types': manifestTypes,
                'real_asset_deployment_public_ids': realAssetDeploymentPublicIds,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Manifest
     * @returns CreateManifestUploadTaskResponse Returns an authenticated upload URL, and atask ID that can be used to check progress of ingesting data from blob storage into the db
     * @throws ApiError
     */
    public createManifest({
        name,
        dataVolumeId,
        manifestType,
        realAssetDeploymentPublicId,
    }: {
        name: string,
        dataVolumeId: string,
        manifestType?: ManifestType,
        realAssetDeploymentPublicId?: string,
    }): CancelablePromise<CreateManifestUploadTaskResponse> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/manifest',
            query: {
                'name': name,
                'data_volume_id': dataVolumeId,
                'manifest_type': manifestType,
                'real_asset_deployment_public_id': realAssetDeploymentPublicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Manifest Transfer Config
     * @returns ManifestTransferConfigResponse Generates a transfer config for uploading data for a manifest via object_store
     * @throws ApiError
     */
    public getManifestTransferConfig({
        name,
        dataVolumeId,
        manifestType,
        read = false,
        write = false,
    }: {
        name: string,
        dataVolumeId: string,
        manifestType?: ManifestType,
        read?: boolean,
        write?: boolean,
    }): CancelablePromise<ManifestTransferConfigResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/manifest/transfer_config',
            query: {
                'name': name,
                'data_volume_id': dataVolumeId,
                'manifest_type': manifestType,
                'read': read,
                'write': write,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Manifest Detail
     * @returns void
     * @throws ApiError
     */
    public manifestDetail({
        publicId,
        download,
    }: {
        publicId: string,
        download: boolean,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/manifest/{public_id}',
            path: {
                'public_id': publicId,
            },
            query: {
                'download': download,
            },
            errors: {
                307: `Return manifest JSON by public id`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Manifest
     * @returns any Delete a manifest
     * @throws ApiError
     */
    public deleteManifest({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/manifest/{public_id}',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Manifest Metadata
     * @returns ManifestResponseWithDataVolume Manifest metadata
     * @throws ApiError
     */
    public getManifestMetadata({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<ManifestResponseWithDataVolume> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/manifest/{public_id}/metadata',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Seal Manifest
     * @returns ManifestResponse Mark this manifest as 'sealed', locking it from further updates
     * @throws ApiError
     */
    public sealManifest({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<ManifestResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/manifest/{public_id}/seal',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Unseal Manifest
     * @returns ManifestResponse Mark this manifest as 'unsealed', allowing it to be updated in-place
     * @throws ApiError
     */
    public unsealManifest({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<ManifestResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/manifest/{public_id}/unseal',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Search Data Assets
     * @returns SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_ Successful Response
     * @throws ApiError
     */
    public searchDataAssets({
        query,
        sealogEventQuery,
        exclude,
        regex = false,
        fileExtensions,
        directory,
        parentPrefix,
        includeDescendantDeployments = false,
        squashByPath = false,
        dataSetPublicIds,
        dataAssetPublicIds,
        manifestTypes,
        manifestPublicIds,
        realAssetPublicIds,
        realAssetDeploymentPublicIds,
        excludedRealAssetDeploymentPublicIds,
        storageTiers,
        storageTierStatuses,
        sealogEventValues,
        sealogEventFreeTexts,
        sealogEventOptionNames,
        sealogEventOptionValues,
        recordModifiedBefore,
        recordModifiedAfter,
        assetModifiedBefore,
        assetModifiedAfter,
        sortField = 'asset_updated_dt',
        sortOrder = 'desc',
        page = 1,
        size = 50,
    }: {
        query?: string,
        sealogEventQuery?: string,
        exclude?: string,
        regex?: boolean,
        fileExtensions?: Array<string>,
        directory?: string,
        parentPrefix?: string,
        includeDescendantDeployments?: boolean,
        squashByPath?: boolean,
        dataSetPublicIds?: Array<string>,
        dataAssetPublicIds?: Array<string>,
        manifestTypes?: Array<ManifestType>,
        manifestPublicIds?: Array<string>,
        realAssetPublicIds?: Array<string>,
        realAssetDeploymentPublicIds?: Array<string>,
        excludedRealAssetDeploymentPublicIds?: Array<string>,
        storageTiers?: Array<StorageTier>,
        storageTierStatuses?: Array<StorageTierStatus>,
        sealogEventValues?: Array<string>,
        sealogEventFreeTexts?: Array<string>,
        sealogEventOptionNames?: Array<string>,
        sealogEventOptionValues?: Array<string>,
        recordModifiedBefore?: string,
        recordModifiedAfter?: string,
        assetModifiedBefore?: string,
        assetModifiedAfter?: string,
        sortField?: 'asset_updated_dt' | 'record_updated_dt' | 'path',
        sortOrder?: 'asc' | 'desc',
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_asset/search',
            query: {
                'query': query,
                'sealog_event_query': sealogEventQuery,
                'exclude': exclude,
                'regex': regex,
                'file_extensions': fileExtensions,
                'directory': directory,
                'parent_prefix': parentPrefix,
                'include_descendant_deployments': includeDescendantDeployments,
                'squash_by_path': squashByPath,
                'data_set_public_ids': dataSetPublicIds,
                'data_asset_public_ids': dataAssetPublicIds,
                'manifest_types': manifestTypes,
                'manifest_public_ids': manifestPublicIds,
                'real_asset_public_ids': realAssetPublicIds,
                'real_asset_deployment_public_ids': realAssetDeploymentPublicIds,
                'excluded_real_asset_deployment_public_ids': excludedRealAssetDeploymentPublicIds,
                'storage_tiers': storageTiers,
                'storage_tier_statuses': storageTierStatuses,
                'sealog_event_values': sealogEventValues,
                'sealog_event_free_texts': sealogEventFreeTexts,
                'sealog_event_option_names': sealogEventOptionNames,
                'sealog_event_option_values': sealogEventOptionValues,
                'record_modified_before': recordModifiedBefore,
                'record_modified_after': recordModifiedAfter,
                'asset_modified_before': assetModifiedBefore,
                'asset_modified_after': assetModifiedAfter,
                'sort_field': sortField,
                'sort_order': sortOrder,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Search Statistics
     * @returns DataAssetSearchStatisticsResponse Successful Response
     * @throws ApiError
     */
    public getSearchStatistics({
        query,
        sealogEventQuery,
        exclude,
        regex = false,
        fileExtensions,
        directory,
        parentPrefix,
        includeDescendantDeployments = false,
        squashByPath = false,
        dataSetPublicIds,
        dataAssetPublicIds,
        manifestTypes,
        manifestPublicIds,
        realAssetPublicIds,
        realAssetDeploymentPublicIds,
        excludedRealAssetDeploymentPublicIds,
        storageTiers,
        storageTierStatuses,
        sealogEventValues,
        sealogEventFreeTexts,
        sealogEventOptionNames,
        sealogEventOptionValues,
        recordModifiedBefore,
        recordModifiedAfter,
        assetModifiedBefore,
        assetModifiedAfter,
        sortField = 'asset_updated_dt',
        sortOrder = 'desc',
    }: {
        query?: string,
        sealogEventQuery?: string,
        exclude?: string,
        regex?: boolean,
        fileExtensions?: Array<string>,
        directory?: string,
        parentPrefix?: string,
        includeDescendantDeployments?: boolean,
        squashByPath?: boolean,
        dataSetPublicIds?: Array<string>,
        dataAssetPublicIds?: Array<string>,
        manifestTypes?: Array<ManifestType>,
        manifestPublicIds?: Array<string>,
        realAssetPublicIds?: Array<string>,
        realAssetDeploymentPublicIds?: Array<string>,
        excludedRealAssetDeploymentPublicIds?: Array<string>,
        storageTiers?: Array<StorageTier>,
        storageTierStatuses?: Array<StorageTierStatus>,
        sealogEventValues?: Array<string>,
        sealogEventFreeTexts?: Array<string>,
        sealogEventOptionNames?: Array<string>,
        sealogEventOptionValues?: Array<string>,
        recordModifiedBefore?: string,
        recordModifiedAfter?: string,
        assetModifiedBefore?: string,
        assetModifiedAfter?: string,
        sortField?: 'asset_updated_dt' | 'record_updated_dt' | 'path',
        sortOrder?: 'asc' | 'desc',
    }): CancelablePromise<DataAssetSearchStatisticsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_asset/search/statistics',
            query: {
                'query': query,
                'sealog_event_query': sealogEventQuery,
                'exclude': exclude,
                'regex': regex,
                'file_extensions': fileExtensions,
                'directory': directory,
                'parent_prefix': parentPrefix,
                'include_descendant_deployments': includeDescendantDeployments,
                'squash_by_path': squashByPath,
                'data_set_public_ids': dataSetPublicIds,
                'data_asset_public_ids': dataAssetPublicIds,
                'manifest_types': manifestTypes,
                'manifest_public_ids': manifestPublicIds,
                'real_asset_public_ids': realAssetPublicIds,
                'real_asset_deployment_public_ids': realAssetDeploymentPublicIds,
                'excluded_real_asset_deployment_public_ids': excludedRealAssetDeploymentPublicIds,
                'storage_tiers': storageTiers,
                'storage_tier_statuses': storageTierStatuses,
                'sealog_event_values': sealogEventValues,
                'sealog_event_free_texts': sealogEventFreeTexts,
                'sealog_event_option_names': sealogEventOptionNames,
                'sealog_event_option_values': sealogEventOptionValues,
                'record_modified_before': recordModifiedBefore,
                'record_modified_after': recordModifiedAfter,
                'asset_modified_before': assetModifiedBefore,
                'asset_modified_after': assetModifiedAfter,
                'sort_field': sortField,
                'sort_order': sortOrder,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Download Asset From Cloud
     * @returns void
     * @throws ApiError
     */
    public downloadAssetFromCloud({
        publicId,
        download,
    }: {
        publicId: string,
        download: boolean,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_asset/{public_id}',
            path: {
                'public_id': publicId,
            },
            query: {
                'download': download,
            },
            errors: {
                307: `Successful Response`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Share Data Asset
     * @returns SharedLinkResponse Generate sharing links for data asset downloads
     * @throws ApiError
     */
    public shareDataAsset({
        publicId,
        lifetimeS = 3600,
    }: {
        publicId: string,
        lifetimeS?: number,
    }): CancelablePromise<SharedLinkResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data_asset/{public_id}/share',
            path: {
                'public_id': publicId,
            },
            query: {
                'lifetime_s': lifetimeS,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Thumbnail
     * @returns any Successful Response
     * @throws ApiError
     */
    public getThumbnail({
        publicId,
        timestamp,
        width,
        height,
    }: {
        publicId: string,
        timestamp?: number,
        width?: number,
        height?: number,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_asset/{public_id}/thumbnail',
            path: {
                'public_id': publicId,
            },
            query: {
                'timestamp': timestamp,
                'width': width,
                'height': height,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Tree Directories
     * @returns SQLModelPage_DataAssetTreeDirectoryResponse_ Successful Response
     * @throws ApiError
     */
    public listTreeDirectories({
        realAssetDeploymentPublicId,
        manifestTypes,
        directory,
        searchTerm,
        recursive = false,
        assetModifiedAfter,
        assetModifiedBefore,
        page = 1,
        size = 50,
    }: {
        realAssetDeploymentPublicId: string,
        manifestTypes?: Array<ManifestType>,
        directory?: string,
        searchTerm?: string,
        recursive?: boolean,
        assetModifiedAfter?: string,
        assetModifiedBefore?: string,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_DataAssetTreeDirectoryResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_asset/tree/{real_asset_deployment_public_id}/directories',
            path: {
                'real_asset_deployment_public_id': realAssetDeploymentPublicId,
            },
            query: {
                'manifest_types': manifestTypes,
                'directory': directory,
                'search_term': searchTerm,
                'recursive': recursive,
                'asset_modified_after': assetModifiedAfter,
                'asset_modified_before': assetModifiedBefore,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
